import type { IngestionPhaseInfo, Project } from '../types/platform';
import { understandingStillRunning } from './queryPolling';
import { isBootstrapGateLocked } from './bootstrapGateLock';

const RUNNING_INGEST_STATUSES = new Set(['running', 'pending', 'queued', 'in_progress']);
const BOOTSTRAP_PROJECT_STATUSES = new Set([
  'planning',
  'ingesting',
  'analyzing',
  'transforming',
  'validating',
]);

/** Minimal workflow slice — avoids importing the full API module (circular with api.ts). */
export interface IngestionWorkflowSnapshot {
  active_workers?: Array<{ id: string; status: string; phase?: string }>;
}

/** Live understanding swarm status (subset of UnderstandingStatus). */
export interface UnderstandingSnapshot {
  parent_status?: string;
  workers?: Array<{ status: string }>;
}

function normalizeIngestionStatus(status?: string): string {
  return (status || '').trim().toLowerCase();
}

function workerStatusActive(status?: string): boolean {
  const st = (status || '').toLowerCase();
  return st === 'running' || st === 'pending' || st === 'queued' || st === 'in_progress' || st === 'processing';
}

export function isIngestionFailed(project?: Pick<Project, 'status' | 'ingestionStatus'>): boolean {
  if (!project) return false;
  return project.status === 'failed' || normalizeIngestionStatus(project.ingestionStatus) === 'failed';
}

/** Backend or workflow still driving Phase-0 repository intake / scan. */
export function isIngestionRunning(
  status?: string,
  ingestionStatus?: string,
  workflow?: IngestionWorkflowSnapshot | null,
): boolean {
  if (status === 'ingesting') return true;
  const ingest = normalizeIngestionStatus(ingestionStatus);
  if (RUNNING_INGEST_STATUSES.has(ingest)) return true;
  return workflowIngestionTaskRunning(workflow);
}

export function workflowIngestionTaskRunning(workflow?: IngestionWorkflowSnapshot | null): boolean {
  const worker = workflow?.active_workers?.find((w) => w.id === 'ingestion.phase0');
  if (!worker) return false;
  return workerStatusActive(worker.status);
}

/** Repository scan finished — distinct from full Discover phase or understanding workers. */
export function isIngestionCompleted(
  project?: Pick<Project, 'status' | 'ingestionStatus'>,
  workflow?: IngestionWorkflowSnapshot | null,
): boolean {
  if (project?.status === 'ingesting') return false;
  const ingest = normalizeIngestionStatus(project?.ingestionStatus);
  if (RUNNING_INGEST_STATUSES.has(ingest)) return false;
  if (workflowIngestionTaskRunning(workflow)) return false;
  if (ingest === 'completed') return true;
  const worker = workflow?.active_workers?.find((w) => w.id === 'ingestion.phase0');
  if (worker && (worker.status || '').toLowerCase() === 'completed') return true;
  return false;
}

function understandWorkersActive(workflow?: IngestionWorkflowSnapshot | null): boolean {
  return workflow?.active_workers?.some((w) => w.phase === 'understand' && workerStatusActive(w.status)) ?? false;
}

function anyWorkflowWorkersActive(workflow?: IngestionWorkflowSnapshot | null): boolean {
  return workflow?.active_workers?.some((w) => workerStatusActive(w.status)) ?? false;
}

/** Post-ingest bootstrap (insights / understanding) while UI status is still "in progress". */
export function isPostIngestBootstrapInProgress(
  project?: Pick<Project, 'status' | 'ingestionStatus'>,
  workflow?: IngestionWorkflowSnapshot | null,
  understanding?: UnderstandingSnapshot | null,
): boolean {
  if (!project || isIngestionFailed(project)) return false;
  if (project.status === 'ams') return false;
  if (!isIngestionCompleted(project, workflow)) return false;
  if (understandingStillRunning(understanding)) return true;
  if (understandWorkersActive(workflow)) return true;
  if (anyWorkflowWorkersActive(workflow)) return true;
  if (project.status && BOOTSTRAP_PROJECT_STATUSES.has(project.status)) return true;
  return false;
}

/**
 * Full-page gate — Phase-0 repository intake / scan only.
 * Understanding workers run in the workspace after the gate dismisses.
 */
export function shouldShowIngestionGate(
  project?: Project,
  workflow?: IngestionWorkflowSnapshot | null,
  _understanding?: UnderstandingSnapshot | null,
  projectId?: string,
): boolean {
  if (!project) return false;
  if (isIngestionFailed(project)) return true;
  if (isIngestionCompleted(project, workflow)) return false;
  // Session lock covers optimistic upload before project status catches up.
  if (projectId && isBootstrapGateLocked(projectId)) return true;
  return isIngestionRunning(project.status, project.ingestionStatus, workflow);
}

/** Dismiss the full-page gate once Phase-0 scan/index has finished. */
export function isIngestionGateComplete(
  project?: Pick<Project, 'status' | 'ingestionStatus'>,
  workflow?: IngestionWorkflowSnapshot | null,
): boolean {
  if (!project || isIngestionFailed(project)) return false;
  return isIngestionCompleted(project, workflow);
}

/** @deprecated Use isIngestionGateComplete — gate no longer waits for ams. */
export function isProjectBootstrapComplete(
  project?: Pick<Project, 'status' | 'ingestionStatus'>,
  workflow?: IngestionWorkflowSnapshot | null,
  _understanding?: UnderstandingSnapshot | null,
): boolean {
  return isIngestionGateComplete(project, workflow);
}

/** Poll project + summary while Phase-0 intake may still be running. */
export function shouldPollProjectDuringIngestion(project?: Project): boolean {
  if (!project) return false;
  if (isIngestionCompleted(project)) return false;
  if (project.status === 'ingesting') return true;
  const ingest = normalizeIngestionStatus(project.ingestionStatus);
  return RUNNING_INGEST_STATUSES.has(ingest);
}

/** Optimistic project patch immediately after a new archive upload starts. */
export function projectPatchForUploadStarted(project: Project): Project {
  return {
    ...project,
    status: 'ingesting',
    ingestionStatus: 'running',
    ingestionError: undefined,
  };
}

export interface IngestionProgressInput {
  failed: boolean;
  complete: boolean;
  running: boolean;
  elapsedSec: number;
  fileCount: number;
  hasIndexedFiles: boolean;
}

/**
 * Ingestion-only progress (not Discover handoff / KG). Caps below 100 until complete.
 */
export function computeIngestionProgressPercent(input: IngestionProgressInput): number {
  const { failed, complete, running, elapsedSec, fileCount, hasIndexedFiles } = input;
  if (failed) return Math.min(40, 12 + elapsedSec);
  if (complete) return 100;

  let pct = 8 + Math.min(22, Math.floor(elapsedSec * 0.9));

  if (fileCount > 0) {
    const scanBoost = Math.min(50, Math.floor(Math.log10(fileCount + 1) * 18));
    pct = Math.max(pct, 28 + scanBoost);
  }

  if (hasIndexedFiles || fileCount > 0) {
    pct = Math.max(pct, 58);
  }

  if (running) {
    pct = Math.max(pct, 65);
    pct = Math.min(95, pct + Math.min(12, Math.floor(elapsedSec * 0.35)));
  }

  return Math.min(95, pct);
}

/**
 * Share of the ring each reported ingestion phase spans. Counted phases move
 * through their span by done/total; the others sit at its start.
 */
const PHASE_SPAN: Record<string, [number, number]> = {
  preparing: [2, 10],
  reading_files: [10, 60],
  analysing: [60, 64],
  saving_inventory: [64, 95],
  finalizing: [95, 99],
};

/** The phase a running ingestion reported, or undefined when it reported none. */
export function activeIngestionPhase(
  phase: IngestionPhaseInfo | undefined,
  running: boolean,
): IngestionPhaseInfo | undefined {
  if (!running || !phase || !PHASE_SPAN[phase.phase]) return undefined;
  return phase;
}

/** Progress from the reported phase and its counts (never 100 until complete). */
export function ingestionPhasePercent(phase: IngestionPhaseInfo): number {
  const [from, to] = PHASE_SPAN[phase.phase] ?? [0, 0];
  if (phase.total <= 0) return from;
  const frac = Math.min(1, Math.max(0, phase.done / phase.total));
  return Math.min(99, Math.round(from + (to - from) * frac));
}

/** Screen step the phase belongs to: 0 clone, 1 scan, 2 index. */
export function ingestionPhaseStep(phase: IngestionPhaseInfo): 0 | 1 | 2 {
  if (phase.phase === 'preparing') return 0;
  if (phase.phase === 'reading_files') return 1;
  return 2;
}

/** Plain-language line for the phase, with its real counts. */
export function ingestionPhaseDetail(phase: IngestionPhaseInfo): string {
  const n = (v: number) => v.toLocaleString();
  const of = phase.total > 0 ? `${n(phase.done)} of ${n(phase.total)}` : '';
  switch (phase.phase) {
    case 'preparing':
      return 'Fetching and unpacking the source';
    case 'reading_files':
      return of ? `Read ${of} files` : 'Reading files';
    case 'analysing':
      return 'Building the inventory from the files';
    case 'saving_inventory':
      return of ? `Saving inventory: ${of} records` : 'Saving inventory';
    case 'finalizing':
      return 'Recording the scan';
    default:
      return 'In progress';
  }
}

/** How long ago the phase last reported, when that is long enough to say. */
export function ingestionPhaseQuietFor(phase: IngestionPhaseInfo, nowMs: number, afterMs = 120_000): number | undefined {
  if (!phase.at) return undefined;
  const at = Date.parse(phase.at);
  if (Number.isNaN(at)) return undefined;
  const quiet = nowMs - at;
  return quiet >= afterMs ? quiet : undefined;
}
